import {
  CalculatedLine,
  Component,
  PerpendicularLine,
  PointSlopeLine,
  PointToPointLine,
} from '@paragon/contract-library';
import { findAndCalculateLine } from '@paragon/relative-geometry';
import { KeyToValueOfType } from '@paragon/utilities';

import { ReferenceLineIntersection } from '../../store/slices/applicationSlice';

export type ReferenceLineWithIntersectionComponents = PerpendicularLine | PointToPointLine | PointSlopeLine;
export interface ReferenceLineIntersectionGuidKeys<K extends keyof any> {
  readonly key1: K;
  readonly key2: K;
}
export interface ReferenceLineIntersectionAndKey<K extends keyof any = keyof any> {
  readonly intersection: ReferenceLineIntersection;
  readonly intersectionKeyPair: ReferenceLineIntersectionGuidKeys<K>;
}
type ReferenceLineIntersectionAndKeyInto<T> = ReferenceLineIntersectionAndKey<KeyToValueOfType<T, string | null>>;

export const getReferenceLineEditablePointIntersections = (
  calculatedLine: CalculatedLine,
  component: Component,
):
  | ReferenceLineIntersectionAndKeyInto<PerpendicularLine>[]
  | ReferenceLineIntersectionAndKeyInto<PointToPointLine>[]
  | ReferenceLineIntersectionAndKeyInto<PointSlopeLine>[] => {
  switch (calculatedLine.lineType) {
    case 'PointToPoint': {
      const line1 = findAndCalculateLine(calculatedLine.basePointLine1Guid, component);
      const line2 = findAndCalculateLine(calculatedLine.basePointLine2Guid, component);
      const line3 = findAndCalculateLine(calculatedLine.endPointLine1Guid, component);
      const line4 = findAndCalculateLine(calculatedLine.endPointLine2Guid, component);
      if (line1 === null || line2 === null || line3 === null || line4 === null) return [];
      const basePoint = line1.intersectWithLine(line2);
      const endPoint = line3.intersectWithLine(line4);
      if (!basePoint || !endPoint) return [];
      return [
        {
          intersection: {
            point: basePoint,
            line1Guid: calculatedLine.basePointLine1Guid!,
            line2Guid: calculatedLine.basePointLine2Guid!,
          },
          intersectionKeyPair: {
            key1: 'basePointLine1Guid',
            key2: 'basePointLine2Guid',
          },
        },
        {
          intersection: {
            point: endPoint,
            line1Guid: calculatedLine.endPointLine1Guid!,
            line2Guid: calculatedLine.endPointLine2Guid!,
          },
          intersectionKeyPair: {
            key1: 'endPointLine1Guid',
            key2: 'endPointLine2Guid',
          },
        },
      ];
    }
    case 'PointSlope': {
      const line1 = findAndCalculateLine(calculatedLine.line1Guid, component);
      const line2 = findAndCalculateLine(calculatedLine.line2Guid, component);
      if (line1 === null || line2 === null) return [];
      const point = line1.intersectWithLine(line2);
      if (point === null) return [];
      return [
        {
          intersection: {
            point,
            line1Guid: calculatedLine.line1Guid!,
            line2Guid: calculatedLine.line2Guid!,
          },
          intersectionKeyPair: {
            key1: 'line1Guid',
            key2: 'line2Guid',
          },
        },
      ];
    }
    case 'Perpendicular': {
      const line1 = findAndCalculateLine(calculatedLine.basePointLine1Guid, component);
      const line2 = findAndCalculateLine(calculatedLine.basePointLine2Guid, component);
      if (line1 === null || line2 === null) return [];
      const point = line1.intersectWithLine(line2);
      if (point === null) return [];
      return [
        {
          intersection: {
            point,
            line1Guid: calculatedLine.basePointLine1Guid!,
            line2Guid: calculatedLine.basePointLine2Guid!,
          },
          intersectionKeyPair: {
            key1: 'basePointLine1Guid',
            key2: 'basePointLine2Guid',
          },
        },
      ];
    }
    default:
      return [];
  }
};
